import { addDays, differenceInMilliseconds, isBefore, isEqual, isSameDay } from 'date-fns'
import { WorkPeriodUpdatePayload, WorkPeriodWithSummary } from '../../../../api/work-periods'
import {
  dateToDateInputValue,
  dateToTimeInputValue,
  isTimeInRange,
  oneHour,
  oneMinute,
  timeInputValueToDate,
} from '../../../../utils/date'
import { ManualClockingInputValues, ManualClockingDates, WorkPeriodDatesFormValues } from './types'
import { getDocIdAsString } from '../../../../utils/documents'
import { validateWorkPeriodTheoreticalRange } from '../../validate-theoretical-range'

/**
 * Calcule les dates de manual clocking ajustées selon la plage horaire de la période de travail
 * Gère les cas où la plage s'étend sur plusieurs jours (équipes de nuit)
 * @param workPeriod - La période de travail
 * @param inputValues - Les valeurs d'heure saisies pour start et end
 * @returns Dates calculées ; `{ start: null, end: null }` si les deux champs manuels sont vides (effacement) ;
 *   `undefined` si `inputValues` est absent ou si aucune date n’a pu être dérivée des saisies
 */
export const calculateManualClockingDate = (
  workPeriod: WorkPeriodWithSummary,
  inputValues?: ManualClockingInputValues,
): ManualClockingDates | undefined => {
  if (!inputValues) {
    return undefined
  }
  // Explicit nulls so submit can send manualClocking: null to the API (clear stored manual times).
  if (!inputValues.startManualClocking && !inputValues.endManualClocking) {
    return { start: null, end: null }
  }

  const workPeriodStartDate = new Date(workPeriod.start.date)
  const workPeriodEndDate = new Date(workPeriod.end.date)
  const isMultiDayPeriod = !isSameDay(workPeriodStartDate, workPeriodEndDate)

  // Convertir les heures saisies en dates
  const startInputDate = inputValues.startManualClocking
    ? timeInputValueToDate(workPeriod.start.date, inputValues.startManualClocking)
    : null
  const endInputDate = inputValues.endManualClocking
    ? timeInputValueToDate(workPeriod.end.date, inputValues.endManualClocking)
    : null

  // Si aucune date valide n'a pu être créée
  if (!startInputDate && !endInputDate) {
    return undefined
  }

  const result: ManualClockingDates = {
    start: null,
    end: null,
  }

  // Traitement du start manual clocking
  if (startInputDate) {
    const isStartInRange = isTimeInRange(startInputDate, workPeriodStartDate, workPeriodEndDate)

    if (isStartInRange && isMultiDayPeriod) {
      const inputHour =
        startInputDate.getHours() * oneHour + startInputDate.getMinutes() * oneMinute
      const endHour =
        workPeriodEndDate.getHours() * oneHour + workPeriodEndDate.getMinutes() * oneMinute
      const midnightHour = 0

      // Cas 2: Entre minuit et end date - ajuster vers le jour de fin
      if (inputHour >= midnightHour && inputHour <= endHour) {
        const adjustedDate = new Date(startInputDate)
        adjustedDate.setFullYear(workPeriodEndDate.getFullYear())
        adjustedDate.setMonth(workPeriodEndDate.getMonth())
        adjustedDate.setDate(workPeriodEndDate.getDate())
        result.start = adjustedDate
      } else {
        // Cas 1: Entre start date et minuit - garder le jour original
        result.start = startInputDate
      }
    } else {
      // Plage normale ou hors plage - garder la date originale
      result.start = startInputDate
    }
  }

  // Traitement du end manual clocking
  if (endInputDate) {
    const isEndInRange = isTimeInRange(endInputDate, workPeriodStartDate, workPeriodEndDate)

    if (isEndInRange && isMultiDayPeriod) {
      const inputHour = endInputDate.getHours() * oneHour + endInputDate.getMinutes() * oneMinute
      const endHour =
        workPeriodEndDate.getHours() * oneHour + workPeriodEndDate.getMinutes() * oneMinute
      const midnightHour = 0

      // Cas 2: Entre minuit et end date - ajuster vers le jour de fin
      if (inputHour >= midnightHour && inputHour <= endHour) {
        const adjustedDate = new Date(endInputDate)
        result.end = adjustedDate
      } else {
        // Cas 1: Entre start date et minuit - garder le jour original
        const adjustedDate = new Date(endInputDate)
        adjustedDate.setFullYear(workPeriodStartDate.getFullYear())
        adjustedDate.setMonth(workPeriodStartDate.getMonth())
        adjustedDate.setDate(workPeriodStartDate.getDate())
        result.end = adjustedDate
      }
    } else {
      // Plage normale ou hors plage - garder la date originale
      result.end = endInputDate
    }
  }

  // Appliquer les jours forcés si fournis
  if (inputValues.forcedStartDay && result.start) {
    result.start = new Date(
      new Date(inputValues.forcedStartDay).setHours(
        result.start.getHours(),
        result.start.getMinutes(),
      ),
    )
  }
  if (inputValues.forcedEndDay && result.end) {
    result.end = new Date(
      new Date(inputValues.forcedEndDay).setHours(result.end.getHours(), result.end.getMinutes()),
    )
  }

  return result
}

/*
  Calculate the manual clocking dates from the form values
  @param values - The form values
  @returns The manual clocking dates
*/
export const calculateManualClockingDateFromFormValues = (
  workPeriod: WorkPeriodWithSummary,
  values: WorkPeriodDatesFormValues,
): ReturnType<typeof calculateManualClockingDate> => {
  return calculateManualClockingDate(workPeriod, {
    startManualClocking: values.workPeriod.start.manualClocking,
    endManualClocking: values.workPeriod.end.manualClocking,
    forcedStartDay: values.workPeriod.start.isCustomDay
      ? values.workPeriod.start.manualClockingDay
      : workPeriod.start.manualClocking && dateToDateInputValue(workPeriod.start.manualClocking),
    forcedEndDay: values.workPeriod.end.isCustomDay
      ? values.workPeriod.end.manualClockingDay
      : workPeriod.end.manualClocking && dateToDateInputValue(workPeriod.end.manualClocking),
  })
}

export const buildFormInitialValues = (
  workPeriod: WorkPeriodWithSummary,
): WorkPeriodDatesFormValues => {
  return {
    user: {
      ...workPeriod.user,
      email: workPeriod.user.appAccess === 'none' ? 'offline' : workPeriod.user.email,
    },
    workPeriod: {
      start: {
        date: dateToTimeInputValue(workPeriod.start.date),
        clocking: dateToTimeInputValue(workPeriod.start.clocking && workPeriod.start.clocking.date),
        manualClocking: dateToTimeInputValue(
          workPeriod.start.manualClocking && workPeriod.start.manualClocking,
        ),
        manualClockingDay: dateToDateInputValue(
          new Date(workPeriod.start.manualClocking || workPeriod.start.date),
        ),
        isCustomDay: false,
        comment: workPeriod.start.comments[workPeriod.start.comments.length - 1]?.message || '',
      },
      end: {
        date: dateToTimeInputValue(workPeriod.end.date),
        clocking: dateToTimeInputValue(workPeriod.end.clocking && workPeriod.end.clocking.date),
        manualClocking: dateToTimeInputValue(
          workPeriod.end.manualClocking && workPeriod.end.manualClocking,
        ),
        manualClockingDay: dateToDateInputValue(
          new Date(workPeriod.end.manualClocking || workPeriod.end.date),
        ),
        isCustomDay: false,
        comment: workPeriod.end.comments[workPeriod.end.comments.length - 1]?.message || '',
      },
      manualBreak: workPeriod.manualBreak || null,
    },
  }
}

/**
 * Build theoretical start/end Date from form time values and work period calendar days.
 * Used for validation and submit payload.
 * Returns null for start/end when the form field is empty so required validation can run.
 */
const theoreticalDatesFromFormValues = (
  workPeriod: WorkPeriodWithSummary,
  values: WorkPeriodDatesFormValues,
): { start: Date | null | undefined; end: Date | null | undefined } => {
  const startDate = values.workPeriod.start.date
    ? timeInputValueToDate(workPeriod.start.date, values.workPeriod.start.date)
    : null
  let endDate = values.workPeriod.end.date
    ? timeInputValueToDate(workPeriod.start.date, values.workPeriod.end.date)
    : null

  if (startDate && endDate && isBefore(endDate, startDate)) {
    endDate = addDays(endDate, 1)
  }

  return { start: startDate || null, end: endDate || null }
}

/**
 * Validates form values (theoretical dates, manual clocking, comments).
 *
 * @param workPeriod - The work period (for reference dates and current state).
 * @param values - The form values.
 * @returns Formik errors object.
 */
export const validateFormValues = (
  workPeriod: WorkPeriodWithSummary,
  values: WorkPeriodDatesFormValues,
): Record<string, string> => {
  const errors: Record<string, string> = {}
  const theoretical = theoreticalDatesFromFormValues(workPeriod, values)
  const rangeErrors = validateWorkPeriodTheoreticalRange(
    theoretical.start ?? undefined,
    theoretical.end ?? undefined,
  )
  if (rangeErrors.startDate) errors['workPeriod.start.date'] = rangeErrors.startDate
  if (rangeErrors.endDate) errors['workPeriod.end.date'] = rangeErrors.endDate

  const clockingDates = calculateManualClockingDateFromFormValues(workPeriod, values)

  if (clockingDates) {
    const { start: startManualClocking, end: endManualClocking } = clockingDates
    if (
      startManualClocking &&
      endManualClocking &&
      (isEqual(startManualClocking, endManualClocking) ||
        isBefore(endManualClocking, startManualClocking))
    )
      errors['workPeriod.start.manualClocking'] =
        'La date de début ne peut être après la date de fin'

    // Require comment only when manual clocking is actually being updated (not when only theoretical times changed)
    const serverStartManual = workPeriod.start.manualClocking
      ? new Date(workPeriod.start.manualClocking)
      : null
    const serverEndManual = workPeriod.end.manualClocking
      ? new Date(workPeriod.end.manualClocking)
      : null
    const isValidDate = (d: Date | null) => d != null && !Number.isNaN(d.getTime())
    const startManualChanged =
      !serverStartManual ||
      !isValidDate(serverStartManual) ||
      (startManualClocking == null && serverStartManual != null) ||
      (startManualClocking != null &&
        serverStartManual != null &&
        !isEqual(startManualClocking, serverStartManual))
    const endManualChanged =
      !serverEndManual ||
      !isValidDate(serverEndManual) ||
      (endManualClocking == null && serverEndManual != null) ||
      (endManualClocking != null &&
        serverEndManual != null &&
        !isEqual(endManualClocking, serverEndManual))
    const isStartManualClockingUpdated = startManualClocking && startManualChanged
    const isEndManualClockingUpdated = endManualClocking && endManualChanged

    if (
      isStartManualClockingUpdated &&
      values.workPeriod.start.manualClocking &&
      !values.workPeriod.start.comment
    )
      errors['workPeriod.start.comment'] = 'Champ requis'

    if (
      isEndManualClockingUpdated &&
      values.workPeriod.end.manualClocking &&
      !values.workPeriod.end.comment
    )
      errors['workPeriod.end.comment'] = 'Champ requis'

    if (
      startManualClocking &&
      endManualClocking &&
      differenceInMilliseconds(endManualClocking, startManualClocking) > 24 * oneHour
    ) {
      errors['workPeriod.end.manualClocking'] =
        'La date de fin ne peut pas être plus grande que 24 heures après la date de début'
    }
  }

  return errors
}

/**
 * Build the API payload for work period update from form values.
 * Includes theoretical start/end dates from form and manual clocking when present.
 * Empty manual time fields yield `manualClocking: null` so the backend can clear them.
 */
export const buildSubmitPayload = (
  workPeriod: WorkPeriodWithSummary,
  values: WorkPeriodDatesFormValues,
): WorkPeriodUpdatePayload => {
  const clockingDates = calculateManualClockingDateFromFormValues(workPeriod, values)
  const theoretical = theoreticalDatesFromFormValues(workPeriod, values)

  const newStartDate: WorkPeriodUpdatePayload['start'] = {
    date: (theoretical.start ?? workPeriod.start.date) as Date,
    manualClocking: null,
  }
  if (clockingDates?.start != null) {
    newStartDate.manualClocking = clockingDates.start
    newStartDate.comment = values.workPeriod.start.comment
  } else if (clockingDates === undefined && workPeriod.start.manualClocking) {
    // Calculator could not derive dates (edge case); keep server value.
    newStartDate.manualClocking = workPeriod.start.manualClocking
    newStartDate.comment =
      workPeriod.start.comments[workPeriod.start.comments.length - 1]?.message || ''
  }
  const newEndDate: WorkPeriodUpdatePayload['end'] = {
    date: (theoretical.end ?? workPeriod.end.date) as Date,
    manualClocking: null,
  }
  if (clockingDates?.end != null) {
    newEndDate.manualClocking = clockingDates.end
    newEndDate.comment = values.workPeriod.end.comment
  } else if (clockingDates === undefined && workPeriod.end.manualClocking) {
    newEndDate.manualClocking = workPeriod.end.manualClocking
    newEndDate.comment = workPeriod.end.comments[workPeriod.end.comments.length - 1]?.message || ''
  }
  const manualBreak =
    //@ts-expect-error when the field is cleared it's value is empty string ""
    values.workPeriod.manualBreak === '' ? null : values.workPeriod.manualBreak

  return {
    missionId: getDocIdAsString(workPeriod.mission),
    workPeriodId: getDocIdAsString(workPeriod),
    start: newStartDate,
    end: newEndDate,
    manualBreak,
  }
}

/**
 * True when computed manual clocking (including explicit null) differs from server state.
 */
const manualClockingDiffersFromServer = (
  computed: Date | null | undefined,
  server: Date | undefined | null,
): boolean => {
  const c = computed ?? null
  const s = server ?? null
  if (c == null && s == null) return false
  if (c instanceof Date && s instanceof Date) return c.getTime() !== s.getTime()
  return true
}

export const isManualClockingUpdated = (
  workPeriod: WorkPeriodWithSummary,
  values: WorkPeriodDatesFormValues,
): boolean => {
  const clockingDates = calculateManualClockingDateFromFormValues(workPeriod, values)
  return (
    manualClockingDiffersFromServer(clockingDates?.start, workPeriod.start.manualClocking) ||
    manualClockingDiffersFromServer(clockingDates?.end, workPeriod.end.manualClocking)
  )
}
